import {
  ParticipantView,
  StreamVideoParticipant,
  useCall,
  useCallStateHooks,
} from "@stream-io/video-react-sdk";
import { useEffect, useState, useRef } from "react";
import Loader from "./Loader";
import { showToast } from "../../utils/utils.js";

const SCREEN_SHARE_TRACK_TYPE = 3;

const CallLayout = ({ onScreenShare }: { onScreenShare: any }) => {
  const call = useCall();
  const { useParticipants, useCallState } = useCallStateHooks();
  const participants = useParticipants();
  const callState = useCallState();
  const [loading, setLoading] = useState(true);
  const [callOwner, setCallOwner] = useState<
    StreamVideoParticipant | undefined
  >(undefined);
  const [screenSharingParticipant, setScreenSharingParticipant] =
    useState<StreamVideoParticipant | null>(null);
  const [videoReady, setVideoReady] = useState(false);
  const renderAttemptRef = useRef(0);
  const maxAttempts = 15;
  const participantsRef = useRef(participants);

  // Keep a reference to the participants to prevent unnecessary re-renders
  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);

  // Monitor connection state and participants
  useEffect(() => {
    if (participants.length > 0) {
      // Find the call owner
      const owner = participants.find(
        (p) => p.userId === callState.createdBy?.id
      );

      // Only update if we don't have an owner yet or if it's a different owner
      if (!callOwner || (owner && owner.userId !== callOwner.userId)) {
        setCallOwner(owner);
      }

      // Find the participant sharing their screen
      const screenSharer = participants.find((p) =>
        p.publishedTracks.includes(SCREEN_SHARE_TRACK_TYPE)
      );

      // Only update screen sharer if it changed
      const currentScreenSharerId = screenSharingParticipant?.userId;
      if (screenSharer && screenSharer.userId !== currentScreenSharerId) {
        setScreenSharingParticipant(screenSharer);
        onScreenShare(screenSharer);
      } else if (!screenSharer && screenSharingParticipant) {
        setScreenSharingParticipant(null);
        onScreenShare(null);
      }

      // After participants are found, wait a bit longer before showing UI
      if (loading && !videoReady) {
        setTimeout(() => {
          setVideoReady(true);
          setLoading(false);
        }, 2000);
      }
    }
  }, [
    participants,
    callState.createdBy?.id,
    onScreenShare,
    callOwner,
    screenSharingParticipant,
    loading,
    videoReady,
  ]);

  // Persist video connections by preventing unnecessary re-renders
  const stableCallOwnerKey = useRef(`video-${callOwner?.userId || "unknown"}`);
  const stableScreenShareKey = useRef(
    `screen-${screenSharingParticipant?.userId || "unknown"}`
  );

  useEffect(() => {
    if (callOwner?.userId) {
      stableCallOwnerKey.current = `video-${callOwner.userId}`;
    }
    if (screenSharingParticipant?.userId) {
      stableScreenShareKey.current = `screen-${screenSharingParticipant.userId}`;
    }
  }, [callOwner?.userId, screenSharingParticipant?.userId]);

  // Check video streams periodically but with stable references
  useEffect(() => {
    const checkVideoInterval = setInterval(() => {
      if (renderAttemptRef.current < maxAttempts) {
        renderAttemptRef.current += 1;

        // Check if any participant has active video using the ref
        const hasActiveVideo = participantsRef.current.some(
          (p) => p.videoStream?.active || p.screenShareStream?.active
        );

        if (hasActiveVideo && !videoReady) {
          setVideoReady(true);
          setLoading(false);
        }
      } else if (!videoReady) {
        // After max attempts, show the UI anyway
        setVideoReady(true);
        setLoading(false);
      }

      // If we've reached max attempts, clear the interval
      if (renderAttemptRef.current >= maxAttempts) {
        clearInterval(checkVideoInterval);
      }
    }, 1000);

    return () => clearInterval(checkVideoInterval);
  }, [videoReady]);

  // Ensure we eventually show UI even if video never becomes ready
  useEffect(() => {
    // Fallback timer to show UI after a maximum wait time
    const fallbackTimer = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 10000); // 10 seconds maximum wait time

    return () => clearTimeout(fallbackTimer);
  }, [loading]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="relative h-auto sl:h-[50vh] ll:h-full max-h-full w-full max-w-[80rem] mx-auto p-1 sl:p-8">
      {call?.screenShare?.state.mediaStream?.active &&
        screenSharingParticipant && (
          <div className="w-full h-full sl:h-full max-h-[55vh] col-span-1 sl:col-span-2">
            <ParticipantView
              className="w-full h-full rounded-lg sl:rounded-xl shadow-lg aspect-video object-fill"
              key={stableScreenShareKey.current}
              participant={screenSharingParticipant}
              trackType="screenShareTrack"
              // autoPlay
              // muted={false}
            />
          </div>
        )}

      {callOwner && (
        <div className="w-full h-full max-h-[55vh] sl:h-full">
          <ParticipantView
            className="w-full h-full max-h-[55vh] rounded-lg sl:rounded-xl shadow-lg aspect-video"
            key={stableCallOwnerKey.current}
            participant={callOwner}
            trackType="videoTrack"
            // autoPlay
            // muted={false}
          />
        </div>
      )}
    </div>
  );
};

export default CallLayout;

// const CallLayout = ({ onScreenShare }: { onScreenShare: any }) => {
//   const call = useCall();
//   const { useParticipants, useCallState } = useCallStateHooks();
//   const participants = useParticipants();
//   const callState = useCallState();
//   const [loading, setLoading] = useState(false);
//   const [callOwner, setCallOwner] = useState<
//     StreamVideoParticipant | undefined
//   >(undefined);
//   const [screenSharingParticipant, setScreenSharingParticipant] =
//     useState<StreamVideoParticipant | null>(null);

//   // useEffect(() => {
//   //   if (participants.length > 0) {
//   //     // Find the call owner
//   //     const owner = participants.find(
//   //       (p) => p.userId === callState.createdBy?.id
//   //     );
//   //     setCallOwner(owner || undefined);

//   //     // Find the participant sharing their screen
//   //     const screenSharer = participants.find((p) =>
//   //       p.publishedTracks.includes(SCREEN_SHARE_TRACK_TYPE)
//   //     );

//   //     if (screenSharer) {
//   //       setScreenSharingParticipant(screenSharer);
//   //       onScreenShare(screenSharer);
//   //     } else {
//   //       setScreenSharingParticipant(null);
//   //       onScreenShare(null);
//   //     }

//   //     setLoading(false);
//   //   } else {
//   //     setLoading(true);
//   //   }
//   // }, [participants, callState.createdBy?.id]);

//   if (loading) {
//     return <Loader />;
//   }

//   return (
//     <div className="relative h-auto sl:h-[50vh] ll:h-full max-h-full w-full max-w-[80rem] mx-auto p-1 sl:p-8">
//       {call?.screenShare?.state.mediaStream?.active && (
//         <div className="w-full h-full sl:h-full max-h-[55vh] col-span-1 sl:col-span-2">
//           <ParticipantView
//             className="w-full h-full rounded-lg sl:rounded-xl shadow-lg aspect-video object-fill"
//             key={call?.streamClient?.user?.id}
//             participant={participants.find(
//               (p) => p.userId === call?.streamClient.user?.id
//             )}
//             trackType="screenShareTrack"
//           />
//         </div>
//       )}

//       {/* {callOwner && callOwner.videoStream?.active && ( */}
//       <div className="w-full h-full max-h-[55vh] sl:h-full">
//         <ParticipantView
//           className="w-full h-full max-h-[55vh] rounded-lg sl:rounded-xl shadow-lg aspect-video"
//           key={call?.streamClient?.user?.id}
//           participant={participants.find(
//             (p) => p.userId === call?.streamClient.user?.id
//           )}
//           trackType="videoTrack"
//         />
//       </div>
//       {/* )} */}
//     </div>
//   );
// };

// export default CallLayout;

// import {
//   ParticipantView,
//   useCall,
//   useCallStateHooks,
// } from "@stream-io/video-react-sdk";
// import { useEffect, useState } from "react";
// import Loader from "./Loader";
// import CustomCallOwner from "./CustomCallOwner";

// const SCREEN_SHARE_TRACK_TYPE = 3; // Define the screen share track type

// const CallLayout = ({ onScreenShare }: { onScreenShare: any }) => {
//   const { useParticipants, useCallState, useScreenShareState } =
//     useCallStateHooks();
//   const participants = useParticipants();
//   const callState = useCallState();
//   const [loading, setLoading] = useState(true);
//   const [callOwner, setCallOwner] = useState<any | null>(null);
//   const [screenSharingParticipant, setScreenSharingParticipant] = useState<
//     any | null
//   >(null);

//   useEffect(() => {
//     if (participants && participants.length > 0) {
//       setLoading(false);

//       // Find the call owner
//       const owner = participants.find(
//         (p) => p.userId === callState.createdBy?.id
//       );
//       setCallOwner(owner || null);

//       // Find the participant sharing their screen
//       const screenSharer = participants.find((p) =>
//         p.publishedTracks.includes(SCREEN_SHARE_TRACK_TYPE)
//       );

//       if (screenSharer) {
//         setScreenSharingParticipant(screenSharer);
//         onScreenShare(screenSharer); // Notify parent about screen sharer
//       } else {
//         setScreenSharingParticipant(null);
//         onScreenShare(null);
//       }
//     }
//   }, [participants, callState.createdBy?.id, onScreenShare]);

//   return loading ? (
//     <Loader />
//   ) : (
//     <div className="flex flex-wrap m-auto h-[56vh] w-full px-5">
//       {screenSharingParticipant ? (
//         <ParticipantView
//           className="w-full h-full"
//           key={screenSharingParticipant.userId}
//           participant={screenSharingParticipant}
//           trackType="screenShareTrack"
//           ParticipantViewUI={CustomCallOwner}
//         />
//       ) : (
//         callOwner && (
//           <ParticipantView
//             className="w-full h-full"
//             key={callOwner.userId}
//             participant={callOwner}
//             ParticipantViewUI={CustomCallOwner}
//           />
//         )
//       )}
//     </div>
//   );
// };

// export default CallLayout;
